<script lang="ts">
	import { Dropdown, DropdownItem, Helper, Input, Radio, Select } from "flowbite-svelte";
	import { clickOutside } from "flowbite-svelte-v1";
	import { tick } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { ChoiceFieldPropertiesSchema, ChoiceSchema, ChoiceType, FieldPropertiesSchema, FieldSchema, type ChoiceFieldProperties, type Field } from "#gen/forms/v1/field_pb.js";
	import Checkbox from "#lib/components/Checkbox.svelte";
	import ContentEditable from "#lib/components/ContentEditable.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import ChoiceBulkOptionsEditor from "#lib/components/page/components/form/ChoiceBulkOptionsEditor.svelte";
	import DefaultValue from "#lib/components/page/components/form/DefaultValue.svelte";
	import FieldHeader from "#lib/components/page/components/form/FieldHeader.svelte";
	import type { TypeIcon } from "#lib/components/page/components/form/FieldProps.ts";
	import { PRECONFIGURED_OPTIONS, getPreconfiguredOptions, type PreconfiguredOptionKey } from "#lib/components/page/components/form/choicePresets.ts";
	import { getChoiceAsText, getDefaultTextAsStruct } from "#lib/components/page/components/form/defaultValue.ts";
	import { autofocus } from "#lib/directives/autofocus.js";
	import { clone, create } from "#lib/protobuf-compat.js";
	import { ERR_REQUIRED } from "#lib/validation.js";

	type ChoiceValue = string | string[] | boolean | null | undefined;

	interface Props {
		id: string;
		typeIcon?: TypeIcon;
		field: Field;
		value?: ChoiceValue;
		editable?: boolean;
		active?: boolean;
		readOnly?: boolean;
		onInput?: (field: Field) => void;
		onChange?: (field: Field) => void;
	}

	let { typeIcon, field, value = $bindable(), editable = false, active = false, readOnly = false, onInput, onChange, ...rest }: Props = $props();

	const inputDisabled = $derived(editable || readOnly || !field.active);
	const defaultValueString = $derived(getChoiceAsText(field.defaultValue?.value as string | string[] | undefined));

	let error: string | undefined = $state(undefined);
	let otherValue: string | undefined = $state("");
	let otherChecked = $state(false);
	let isBulkEditing = $state(false);
	let presetDropdownOpen = $state(false);

	let properties = $derived(field.properties?.properties.case === "choice" ? (field.properties?.properties.value as ChoiceFieldProperties) : create(ChoiceFieldPropertiesSchema));
	const isSingleCheckbox = $derived(properties.choiceType === ChoiceType.BOOL);
	const isDropdown = $derived(properties.choiceType === ChoiceType.DROPDOWN);
	const singleCheckboxStringValue = $derived(isSingleCheckbox ? properties.choices?.[0]?.value : undefined);
	const boolDefaultValue = $derived(isSingleCheckbox ? ((field.defaultValue?.value as boolean) ?? false) : false);
	const searchable = $derived(properties.searchable ?? false);

	let hasBeenEdited = $state(false);
	let isAdding = $state(false);
	let optionCount = $derived(properties.choices.length + (properties.allowOther ? 1 : 0));
	let optionsList: HTMLDivElement | undefined = $state();
	let searchableDropdownOpen = $state(false);
	let searchQuery = $state("");
	let isFiltering = $state(false);
	let highlightedIndex = $state(-1);
	let searchableListbox: HTMLUListElement | undefined = $state();

	const OTHER_OPTION = "Other";
	const dropdownItems = $derived([...properties.choices.map(choice => ({ value: choice.value, name: choice.value })), ...(properties.allowOther ? [{ value: OTHER_OPTION, name: OTHER_OPTION }] : [])]);
	const filteredDropdownItems = $derived(isFiltering && searchQuery.trim() ? dropdownItems.filter(item => item.name.toLowerCase().includes(searchQuery.trim().toLowerCase())) : dropdownItems);
	const selectedDropdownLabel = $derived(typeof value === "string" && value ? value : "");
	const searchableInputValue = $derived(searchableDropdownOpen ? searchQuery : selectedDropdownLabel);
	const listboxId = $derived(`${field.name}-listbox`);

	$effect(() => {
		const choiceValue = typeof value === "string" || Array.isArray(value) ? value : undefined;
		if (editable && !isSingleCheckbox && defaultValueString != getChoiceAsText(choiceValue)) {
			const nextField = clone(FieldSchema, field);
			nextField.defaultValue = getDefaultTextAsStruct(choiceValue);
			onChange?.(nextField);
		}
	});

	$effect.pre(() => {
		// If the field hasn't been edited, set the default value
		if (!hasBeenEdited) {
			if (properties.choiceType === ChoiceType.CHECKBOX) {
				value = [];
			} else if (isSingleCheckbox) {
				if (typeof field.defaultValue?.value === "boolean") {
					value = field.defaultValue.value;
				} else {
					value = false;
				}
			} else {
				// RADIO and SELECT/DROPDOWN: initialize to null so unselected
				// fields don't produce undefined values when the form is
				// submitted
				value = typeof value === "string" ? value : null;
			}
		} else if (properties.choiceType === ChoiceType.CHECKBOX) {
			if (!Array.isArray(value)) value = [];
		} else if (isSingleCheckbox) {
			if (typeof value !== "boolean") value = false;
		} else if (typeof value !== "string") {
			value = null;
		}
	});

	$effect(() => {
		if (value === otherValue || (properties.choiceType === ChoiceType.CHECKBOX && Array.isArray(value) && otherValue && value.includes(otherValue))) {
			otherChecked = true;
		}
	});

	// If field changes from dropdown, clear bulk editing state
	$effect(() => {
		if (!isDropdown) {
			isBulkEditing = false;
		}
	});

	$effect(() => {
		if (!searchableDropdownOpen) {
			return;
		}
		if (filteredDropdownItems.length === 0) {
			highlightedIndex = -1;
		} else if (highlightedIndex >= filteredDropdownItems.length) {
			highlightedIndex = 0;
		}
	});

	export function validate(): boolean {
		const isEmpty = !value || (properties.choiceType === ChoiceType.CHECKBOX && Array.isArray(value) && value.length === 0);
		if (field.required && isEmpty) {
			error = ERR_REQUIRED;
			return false;
		} else {
			error = "";
		}
		return true;
	}

	// Always build a new field when changing properties to ensure the field object
	// reference changes and triggers reactivity in parent components
	function buildUpdatedField(mutate: (next: ChoiceFieldProperties) => void): Field {
		const nextProps = clone(ChoiceFieldPropertiesSchema, properties);
		mutate(nextProps);
		const nextField = clone(FieldSchema, field);
		if (!nextField.properties) {
			nextField.properties = create(FieldPropertiesSchema);
		}
		nextField.properties.properties = { case: "choice", value: nextProps };
		return nextField;
	}

	function addOption() {
		if (isAdding) return;
		isAdding = true;
		try {
			const next = buildUpdatedField(p => {
				p.choices = [...p.choices, create(ChoiceSchema, { value: "Option " + (p.choices.length + 1) })];
			});
			onChange?.(next);
			scrollOptionsToBottom();
		} finally {
			isAdding = false;
		}
	}

	function addOther() {
		const next = buildUpdatedField(p => {
			p.allowOther = true;
		});
		onChange?.(next);
	}

	async function scrollOptionsToBottom() {
		await tick();
		optionsList?.scrollTo({ top: optionsList.scrollHeight, behavior: "smooth" });
	}

	function applyPreset(key: PreconfiguredOptionKey) {
		presetDropdownOpen = false;
		const values = getPreconfiguredOptions(key);
		const next = buildUpdatedField(p => {
			p.choices = values.map(choice => create(ChoiceSchema, { value: choice }));
		});
		dropStaleValue(values);
		onChange?.(next);
	}

	function dropStaleValue(values: string[]) {
		if (typeof value === "string" && !values.includes(value)) {
			value = null;
		} else if (Array.isArray(value)) {
			value = value.filter(v => values.includes(v));
		}
	}

	function bulkEditOptions() {
		isBulkEditing = true;
	}

	function removeOther() {
		const next = buildUpdatedField(p => {
			p.allowOther = false;
		});
		if (properties.choiceType === ChoiceType.CHECKBOX && Array.isArray(value)) {
			value = value.filter(v => v !== otherValue);
		} else if (value === otherValue) {
			value = "";
		}
		otherValue = "";
		onChange?.(next);
	}

	function onOtherChange() {
		if (!otherValue || !otherValue.trim()) return;
		if (properties.choiceType === ChoiceType.RADIO) {
			value = otherValue;
		} else if (properties.choiceType === ChoiceType.CHECKBOX) {
			if (!Array.isArray(value)) value = [];
			const validOptions = properties.choices.map(o => o.value);
			const filtered = value.filter((v: string) => validOptions.includes(v));
			value = [...filtered, otherValue];
		}
	}

	function removeOption(i: number) {
		const next = buildUpdatedField(p => {
			p.choices = p.choices.filter((_, idx) => idx !== i);
		});
		onChange?.(next);
	}

	// Set by updateChoiceValue so handleFieldInput/handleFieldChange can emit the
	// updated field without mutating the prop. Cleared after each use.
	let pendingField: Field | undefined;

	function updateChoiceValue(index: number, nextValue: string | undefined) {
		if (!properties.choices[index]) return;
		hasBeenEdited = true;
		pendingField = buildUpdatedField(p => {
			const target = p.choices[index];
			if (target) target.value = nextValue ?? "";
		});
	}

	function handleFieldInput() {
		hasBeenEdited = true;
		onInput?.(pendingField ?? field);
		pendingField = undefined;
	}

	function handleFieldChange() {
		onChange?.(pendingField ?? field);
		pendingField = undefined;
	}

	function handleBoolDefaultChange(checked: boolean) {
		value = checked;
		if (editable) {
			const nextField = clone(FieldSchema, field);
			nextField.defaultValue = { value: checked };
			onChange?.(nextField);
		}
	}

	function handleSaveBulkEdit(values: string[]) {
		const next = buildUpdatedField(p => {
			p.choices = values.map(value => create(ChoiceSchema, { value }));
		});
		dropStaleValue(values);
		onChange?.(next);
	}

	function onOtherCheckboxChange() {
		if (otherChecked) {
			if (properties.choiceType === ChoiceType.RADIO) {
				value = otherValue ?? "";
			} else if (properties.choiceType === ChoiceType.CHECKBOX) {
				// Remove other option from values
				const current = Array.isArray(value) ? value : [];
				const validOptions = properties.choices.map(o => o.value);
				const filtered = current.filter((v: string) => validOptions.includes(v));
				value = [...filtered, otherValue ?? ""];
			}
		}
	}

	function openSearchableDropdown() {
		if (inputDisabled) {
			return;
		}
		isFiltering = false;
		searchQuery = selectedDropdownLabel;
		searchableDropdownOpen = true;
		const selectedIndex = dropdownItems.findIndex(item => item.value === value);
		highlightedIndex = dropdownItems.length === 0 ? -1 : Math.max(0, selectedIndex);
	}

	function closeSearchableDropdown() {
		searchableDropdownOpen = false;
		isFiltering = false;
		searchQuery = "";
		highlightedIndex = -1;
		if (!editable) {
			validate();
		}
	}

	function selectSearchableItem(item: { value: string; name: string }) {
		hasBeenEdited = true;
		value = item.value;
		closeSearchableDropdown();
	}

	function handleSearchInput(event: Event) {
		if (inputDisabled) {
			return;
		}
		searchQuery = (event.target as HTMLInputElement).value;
		isFiltering = true;
		searchableDropdownOpen = true;
		highlightedIndex = filteredDropdownItems.length > 0 ? 0 : -1;
	}

	async function scrollHighlightedOptionIntoView() {
		await tick();
		searchableListbox?.querySelector<HTMLElement>("[aria-selected='true']")?.scrollIntoView({ block: "nearest" });
	}

	function handleSearchKeydown(event: KeyboardEvent) {
		if (inputDisabled) {
			return;
		}
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				if (!searchableDropdownOpen) {
					openSearchableDropdown();
					return;
				}
				if (filteredDropdownItems.length === 0) {
					return;
				}
				highlightedIndex = highlightedIndex < filteredDropdownItems.length - 1 ? highlightedIndex + 1 : 0;
				void scrollHighlightedOptionIntoView();
				break;
			case "ArrowUp":
				event.preventDefault();
				if (!searchableDropdownOpen) {
					openSearchableDropdown();
					return;
				}
				if (filteredDropdownItems.length === 0) {
					return;
				}
				highlightedIndex = highlightedIndex > 0 ? highlightedIndex - 1 : filteredDropdownItems.length - 1;
				void scrollHighlightedOptionIntoView();
				break;
			case "Home":
				if (searchableDropdownOpen && filteredDropdownItems.length > 0) {
					event.preventDefault();
					highlightedIndex = 0;
					void scrollHighlightedOptionIntoView();
				}
				break;
			case "End":
				if (searchableDropdownOpen && filteredDropdownItems.length > 0) {
					event.preventDefault();
					highlightedIndex = filteredDropdownItems.length - 1;
					void scrollHighlightedOptionIntoView();
				}
				break;
			case "Enter":
				if (searchableDropdownOpen && highlightedIndex >= 0 && filteredDropdownItems[highlightedIndex]) {
					event.preventDefault();
					selectSearchableItem(filteredDropdownItems[highlightedIndex]);
				}
				break;
			case "Escape":
				if (searchableDropdownOpen) {
					event.preventDefault();
					event.stopPropagation();
					closeSearchableDropdown();
				}
				break;
			case "Tab":
				if (searchableDropdownOpen) {
					closeSearchableDropdown();
				}
				break;
		}
	}
</script>

<div {...rest}>
	{#if editable}
		<FieldHeader {typeIcon} {active} {field} on:input={() => handleFieldInput()} on:change={() => handleFieldChange()} />
	{:else if !isSingleCheckbox}
		<label for={field.name} class="mb-2 inline-block text-sm font-medium" class:required={field.required}>
			{field.title}
		</label>
		{#if field.description}
			<div class="field-description my-2 text-xs text-gray-500">{field.description}</div>
		{/if}
	{/if}
	{#if field.static && !editable}
		{#if isSingleCheckbox}
			<Checkbox disabled={true} class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""} checked={boolDefaultValue}>
				{singleCheckboxStringValue ?? ""}
			</Checkbox>
		{:else}
			<DefaultValue>{isSingleCheckbox ? boolDefaultValue : defaultValueString}</DefaultValue>
		{/if}
	{:else if properties.choiceType == ChoiceType.RADIO || properties.choiceType == ChoiceType.CHECKBOX || isSingleCheckbox}
		<ul class="divide-gray-20 w-full divide-y rounded-lg border border-gray-200 dark:divide-gray-600 dark:border-gray-600">
			{#each properties.choices as choice, i (i)}
				<li class="p-3.5">
					<div class="flex items-center">
						<div class="grow">
							{#if properties.choiceType == ChoiceType.RADIO}
								<Radio class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""} bind:group={value as string} value={choice.value} name={field.name} id={field.name} disabled={inputDisabled}>
									{#if editable && !field.immutable}
										<ContentEditable
											value={choice.value}
											class="text-gray-500"
											onChange={next => {
												updateChoiceValue(i, next);
												handleFieldInput();
											}}
											onCommit={next => {
												updateChoiceValue(i, next);
												handleFieldChange();
											}}
										/>
									{:else}
										{choice.value}
									{/if}
								</Radio>
							{:else if isSingleCheckbox}
								<div class="group flex items-center justify-between">
									<Checkbox class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""} checked={boolDefaultValue} name={field.name} id={field.name} disabled={inputDisabled} on:change={e => handleBoolDefaultChange((e.target as HTMLInputElement)?.checked ?? false)}>
										{#if editable && !field.immutable}
											<ContentEditable
												value={choice.value}
												class="text-gray-500"
												onChange={next => {
													updateChoiceValue(i, next);
													handleFieldInput();
												}}
												onCommit={next => {
													updateChoiceValue(i, next);
													handleFieldChange();
												}}
											/>
										{:else}
											{choice.value}{field.required ? "*" : ""}
										{/if}
									</Checkbox>
									{#if editable && !field.immutable}
										{@const defaultTitle = boolDefaultValue ? "Clear default" : "Default to checked"}
										<button type="button" class="text-primary-700 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-medium opacity-0 transition-opacity group-hover:opacity-100 hover:underline" data-action-name="choice-clear-default" data-action-title={defaultTitle} onclick={() => handleBoolDefaultChange(!boolDefaultValue)}>{defaultTitle}</button>
									{/if}
								</div>
							{:else}
								<Checkbox class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""} bind:group={value as string[]} value={choice.value} name={field.name} id={field.name} disabled={inputDisabled}>
									{#if editable && !field.immutable}
										<ContentEditable
											value={choice.value}
											class="text-gray-500"
											onChange={next => {
												updateChoiceValue(i, next);
												handleFieldInput();
											}}
											onCommit={next => {
												updateChoiceValue(i, next);
												handleFieldChange();
											}}
										/>
									{:else}
										{choice.value}
									{/if}
								</Checkbox>
							{/if}
						</div>
						{#if editable && !field.immutable && !isSingleCheckbox}
							<div class="flex-none">
								<button type="button" onclick={() => removeOption(i)}>
									<Icon name="x-mark" class="ml-2 inline-block h-5 w-5" />
								</button>
							</div>
						{/if}
					</div>
				</li>
			{/each}
			{#if properties.allowOther}
				<li class={otherChecked ? "px-3.5 py-1.75" : "p-3.5"}>
					<div class="flex items-center">
						<div class="grow">
							{#if properties.choiceType === ChoiceType.RADIO}
								<Radio class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""} bind:group={value as string} value={otherValue} name={field.name} id={field.name} disabled={inputDisabled} bind:checked={otherChecked} on:change={onOtherCheckboxChange}>
									<div class="flex grow flex-row items-center">
										{#if editable}
											<div class="mr-2 flex w-full flex-row items-end">
												Other:
												<div class="ml-2 w-full border-b border-gray-300 opacity-100"></div>
											</div>
										{:else}
											<div class="mr-2">Other</div>
										{/if}
										{#if otherChecked && !editable}
											<div use:autofocus class="w-full">
												<Input required size="sm" class={twMerge("w-full border-b py-1 placeholder-black outline-none", readOnly ? "disabled:pointer-events-none disabled:cursor-pointer" : "")} color={error ? "red" : "base"} type="text" bind:value={otherValue} name={field.name} id={field.name} disabled={inputDisabled} on:input={onOtherChange} />
											</div>
										{/if}
									</div>
								</Radio>
							{:else}
								<Checkbox class={readOnly ? "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100" : ""} bind:group={value as string[]} value={otherValue} name={field.name} id={field.name} disabled={inputDisabled} bind:checked={otherChecked} on:change={onOtherCheckboxChange}>
									<div class="flex grow flex-row items-center">
										{#if editable}
											<div class="mr-2 flex w-full flex-row items-end">
												Other:
												<div class="ml-2 w-full border-b border-gray-300 opacity-100"></div>
											</div>
										{:else}
											<div class="mr-2">Other</div>
										{/if}
										{#if otherChecked && !editable}
											<div use:autofocus class="w-full">
												<Input required size="sm" class={twMerge("w-full border-b py-1 placeholder-black outline-none", readOnly ? "disabled:pointer-events-none disabled:cursor-pointer" : "")} color={error ? "red" : "base"} type="text" bind:value={otherValue} name={field.name} id={field.name} disabled={inputDisabled} on:input={onOtherChange} />
											</div>
										{/if}
									</div>
								</Checkbox>
							{/if}
						</div>
						{#if editable && !field.immutable && !isSingleCheckbox}
							<div class="flex-none">
								<button type="button" onclick={removeOther}>
									<Icon name="x-mark" class="ml-2 inline-block h-5 w-5" />
								</button>
							</div>
						{/if}
					</div>
				</li>
			{/if}
		</ul>
	{:else}
		{#if searchable}
			<div class="relative" use:clickOutside={() => searchableDropdownOpen && closeSearchableDropdown()}>
				<!-- Tight wrapper so the appearance editor can paint a recolorable dropdown chevron over the trigger. -->
				<div class="relative">
					<Input
						class={twMerge("pr-8", editable && !field.immutable && properties.choices.length && "rounded-b-none", readOnly && "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100")}
						color={error ? "red" : "base"}
						placeholder="Search for an option ..."
						value={searchableInputValue}
						type="text"
						id={field.name}
						disabled={inputDisabled}
						role="combobox"
						aria-autocomplete="list"
						aria-haspopup="listbox"
						aria-expanded={searchableDropdownOpen}
						aria-controls={listboxId}
						aria-invalid={error ? true : undefined}
						on:focus={openSearchableDropdown}
						on:input={handleSearchInput}
						on:keydown={handleSearchKeydown}
					/>
					<svg class="form-select-chevron pointer-events-none absolute top-1/2 right-3 size-[0.75em] -translate-y-1/2 text-sm text-gray-500" viewBox="0 0 10 6" fill="none" aria-hidden="true">
						<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 4 4 4-4" />
					</svg>
				</div>
				{#if searchableDropdownOpen}
					<ul bind:this={searchableListbox} id={listboxId} role="listbox" class="form-choice-listbox absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow dark:border-gray-600 dark:bg-gray-700" aria-label={field.title}>
						{#each filteredDropdownItems as item, i (item.value + i)}
							<li id={`${field.name}-option-${i}`} role="option" aria-selected={highlightedIndex === i} class={twMerge("first:rounded-t-lg last:rounded-b-lg", highlightedIndex === i && "bg-gray-100 dark:bg-gray-600")}>
								<button type="button" tabindex="-1" class="w-full cursor-pointer border-0 bg-transparent px-3 py-2 text-left text-sm text-(--form-input-text-color,#111827)" onmousedown={e => e.preventDefault()} onmouseenter={() => (highlightedIndex = i)} onclick={() => selectSearchableItem(item)}>
									{item.name}
								</button>
							</li>
						{:else}
							<li role="presentation" class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matching options</li>
						{/each}
					</ul>
				{/if}
			</div>
		{:else}
			<!-- Tight wrapper so the appearance editor can paint a recolorable dropdown chevron over the select. -->
			<div>
				<Select color={error ? "red" : "base"} bind:value={() => value ?? undefined, v => (value = v || null)} items={dropdownItems} name={field.name} id={field.name} disabled={inputDisabled} class={twMerge(editable && !field.immutable && properties.choices.length && "rounded-b-none", readOnly && "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100")} />
			</div>
		{/if}
		{#if editable && !field.immutable}
			{#if isBulkEditing}
				<div class="w-full divide-y divide-gray-200 rounded-b-lg border-r border-b border-l border-gray-200 p-4 dark:divide-gray-600 dark:border-gray-600">
					<ChoiceBulkOptionsEditor bind:isBulkEditing onsave={handleSaveBulkEdit} options={properties.choices.map(choice => choice.value)} />
				</div>
			{:else}
				<div bind:this={optionsList} class="max-h-53 overflow-y-auto rounded-b-lg border-b">
					<ul class="w-full divide-y divide-gray-200 rounded-b-lg border-r border-l border-gray-200 dark:divide-gray-600 dark:border-gray-600">
						{#each properties.choices as choice, i (i)}
							<li class="p-3">
								<div class="flex items-center">
									<div class="grow">
										<ContentEditable
											value={choice.value}
											class="text-sm text-gray-500"
											onCommit={next => {
												updateChoiceValue(i, next);
												handleFieldChange();
											}}
										/>
									</div>
									{#if editable && !field.immutable}
										<div class="flex-none">
											<button type="button" onclick={() => removeOption(i)}>
												<Icon name="x-mark" class="ml-2 inline-block h-5 w-5" />
											</button>
										</div>
									{/if}
								</div>
							</li>
						{/each}
						{#if properties.allowOther}
							<li class="p-3">
								<div class="flex items-center">
									<div class="grow">
										<ContentEditable value="Other" class="text-sm text-gray-500" onChange={handleFieldInput} onCommit={handleFieldChange} />
									</div>
									{#if editable && !field.immutable}
										<div class="flex-none">
											<button type="button" onclick={removeOther}>
												<Icon name="x-mark" class="ml-2 inline-block h-5 w-5" />
											</button>
										</div>
									{/if}
								</div>
							</li>
						{/if}
					</ul>
				</div>
			{/if}
		{/if}
	{/if}
	{#if error}
		<Helper class="mt-2" color="red"><span class="font-medium">{error}</span></Helper>
	{/if}
	{#if editable && !field.immutable}
		{#if isSingleCheckbox}
			<div class="w-full pt-2"></div>
		{:else if !isBulkEditing}
			<div class="mt-4 mb-2 flex w-full flex-row justify-between">
				<div class="spacing-2 flex flex-row items-center gap-1">
					<button type="button" class="text-primary-700 text-sm font-medium hover:underline disabled:opacity-50" data-action-name="choice-add-option" disabled={isAdding} onclick={addOption}>Add option</button>
					{#if isDropdown}
						<P size="sm">or</P>
						<div>
							<button type="button" class="text-primary-700 text-sm font-medium hover:underline" data-action-name="choice-add-preset">Use preset options</button>
							<Dropdown bind:open={presetDropdownOpen} class="min-w-max rounded shadow">
								{#each PRECONFIGURED_OPTIONS as option (option.key)}
									<DropdownItem data-action-name="choice-add-preset-item" data-action-title={option.label} on:click={() => applyPreset(option.key)}>{option.label}</DropdownItem>
								{/each}
							</Dropdown>
						</div>
					{:else if !properties.allowOther}
						<P size="sm">or</P>
						<button type="button" class="text-primary-700 text-sm font-medium hover:underline" data-action-name="choice-add-other" onclick={addOther}>add "other"</button>
					{/if}
				</div>
				{#if isDropdown}
					<div class="flex flex-row items-center gap-2">
						<P size="sm" class="font-medium text-black">{optionCount} option{optionCount === 1 ? "" : "s"}</P>
						<button type="button" class="text-primary-700 text-sm font-medium hover:underline" data-action-name="choice-bulk-edit" onclick={bulkEditOptions}>Bulk edit options</button>
					</div>
				{/if}
			</div>
		{/if}
	{/if}
	{#if editable && !isBulkEditing}
		<div class="w-full border-b border-gray-200 pt-2"></div>
	{/if}
</div>
